import React, { FC, useCallback, useMemo, useState } from 'react';
import { RequestAttachmentInterface, ServiceProviderInterface } from '../interfaces';
import { convertToFormData } from '../utils';
import { useResponseHandlerService } from './response-handler.service';
import { useTranslation } from './translation.service';
import { useAuth0 } from '@auth0/auth0-react';

const apiUrl = process.env.REACT_APP_API_URL || '';
const serviceRootURL = `${apiUrl}/api/request-attachment`;
const serviceStudyRequestRootURL = `${apiUrl}/api/study-request`;
const headers = {
	Authorization: '',
};

interface RequestAttachmentServiceInterface {
	attachments: RequestAttachmentInterface[];
	getRequestAttachmentsByRequestId: (requestId: string) => Promise<void>;
	removeAttachment: (
		attachmentId: string,
		requestId: string,
		notes: string
	) => Promise<void>;
	addAttachments: (
		requestAttachments: any[],
		requestId: string,
		notes: string
	) => Promise<void>;
}

interface RequestAttachmentProviderInterface extends ServiceProviderInterface {}

const { createContext, useContext } = React;

const RequestAttachmentContext = createContext<RequestAttachmentServiceInterface>(
	{} as RequestAttachmentServiceInterface
);

export const RequestAttachmentProvider: FC<RequestAttachmentProviderInterface> = (
	props
) => {
	const { getIdTokenClaims } = useAuth0();

	const { t } = useTranslation();
	const { addSuccess } = useResponseHandlerService();
	const [attachments, setAttachments] = useState<RequestAttachmentInterface[]>([]);
	const getRequestAttachmentsByRequestId = useCallback(
		async (requestId: string) => {
			const user = await getIdTokenClaims();
			headers.Authorization = user?.__raw || '';

			const res = await getRequestAttachmentsByRequestIdApiCall(requestId);
			if (res) {
				const attachmentsWOCurrentRequestAttachments = attachments.filter(
					(attachment) => attachment.request !== requestId
				);
				setAttachments([...attachmentsWOCurrentRequestAttachments, ...res]);
			}
		},
		[attachments, setAttachments, getIdTokenClaims]
	);

	const removeAttachment = useCallback(
		async (attachmentId: string, requestId: string, notes: string): Promise<void> => {
			const user = await getIdTokenClaims();
			headers.Authorization = user?.__raw || '';

			return removeAttachmentApiCall(attachmentId, requestId, notes).then(() => {
				addSuccess(t('Attachment was successfully removed.'));
				setAttachments(
					attachments.filter((attachment) => attachment.id !== attachmentId)
				);
			});
		},
		[addSuccess, attachments, t, getIdTokenClaims]
	);

	const addAttachments = useCallback(
		async (
			requestAttachments: any[],
			requestId: string,
			notes: string
		): Promise<void> => {
			const user = await getIdTokenClaims();
			headers.Authorization = user?.__raw || '';

			const res = await addAttachmentApiCall(requestAttachments, requestId, notes);
			if (res) {
				addSuccess(t('Attachment was added successfully.'));
				const attachmentsWOCurrentRequestAttachments = attachments.filter(
					(attachment) => attachment.request !== requestId
				);
				setAttachments([...attachmentsWOCurrentRequestAttachments, ...res]);
			}
		},
		[addSuccess, attachments, t, getIdTokenClaims]
	);
	const value = useMemo(
		() => ({
			attachments,
			getRequestAttachmentsByRequestId,
			removeAttachment,
			addAttachments,
		}),
		[attachments]
	);

	return (
		<RequestAttachmentContext.Provider value={value}>
			{props.children}
		</RequestAttachmentContext.Provider>
	);
};

export const useRequestAttachmentService = () => {
	return useContext<RequestAttachmentServiceInterface>(RequestAttachmentContext);
};

const getRequestAttachmentsByRequestIdApiCall = async (
	requestId: string
): Promise<RequestAttachmentInterface[]> => {
	const res = await window.fetch(`${serviceRootURL}/request/${requestId}`, { headers });
	return res.json();
};

const removeAttachmentApiCall = async (
	attachmentId: string,
	requestId: string,
	notes: string
): Promise<void> => {
	await window.fetch(
		`${serviceStudyRequestRootURL}/${requestId}/requestAttachments/delete`,
		{
			method: 'PATCH',
			headers,
			body: JSON.stringify({ id: attachmentId, notes }),
		}
	);
};

const addAttachmentApiCall = async (
	requestAttachments: any[],
	requestId: string,
	notes: string
): Promise<RequestAttachmentInterface[]> => {
	const formData = convertToFormData({ requestAttachments, notes });
	try {
		const res = await window.fetch(
			`${serviceStudyRequestRootURL}/${requestId}/requestAttachments/add`,
			{
				method: 'PATCH',
				body: formData,
				headers,
			}
		);
		return res.json();
	} catch (e) {
		console.error(e);
		throw e;
	}
};
